import { ExpandMore } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Button, CircularProgress, Link, TextField, Typography } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { KeyboardEventHandler, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import InfoIcon from "./InfoIcon";

export type AccordionWithTextFieldAndButtonProps = {
    disabled: boolean
    summaryTextLeft: string
    summaryCounter?: number | undefined
    summaryCounterTooltip: string
    summaryCounterColor?: string | undefined
    expanded: boolean
    onExpandedChange: () => void
    onClick: (text: string) => Promise<void>
    textfieldEmptyText: string
    textFieldInitText?: string | undefined
    validateText?: ((text: string) => string) | undefined
    buttonText: string
    buttonTooltip: string
    clickableLink?: string | undefined
    clickableLinkText?: string | undefined
    inputRef?: React.RefObject<HTMLInputElement> | undefined
    children?: JSX.Element[] | JSX.Element
    childrenBelowButton?: JSX.Element[] | JSX.Element
}

export const AccordionWithTextFieldAndButton = (props: AccordionWithTextFieldAndButtonProps) => {
    const {
        disabled,
        summaryCounterTooltip,
        summaryTextLeft,
        summaryCounter,
        summaryCounterColor,
        expanded,
        onExpandedChange,
        onClick,
        textfieldEmptyText,
        textFieldInitText,
        validateText,
        buttonText,
        buttonTooltip,
        clickableLink,
        clickableLinkText,
        inputRef,
        children,
        childrenBelowButton
    } = props;

    const { t } = useTranslation();

    const [text, setText] = useState(textFieldInitText || '');
    const [clickErrorLabel, setClickErrorLabel] = useState('');
    const [isProcessingClick, setIsProcessingClick] = useState(false);
    const validationError = useMemo(() => validateText !== undefined ? validateText(text) : '', [text, validateText]);

    //if text changes, the click error should be reset
    useEffect(() => {
        setClickErrorLabel('');
    }, [text]);

    const error = t(clickErrorLabel) !== '' ? clickErrorLabel : validationError;

    const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            props.onClick(text);
        }
    }, [text, props]);

    const handleClick = useCallback(() => {
        setIsProcessingClick(true);
        onClick(text)
            .then(() => setClickErrorLabel(''))
            .catch((e) => {
                setClickErrorLabel(e)
            })
            .then(() => {
                setIsProcessingClick(false)
            })
    }, [onClick, text]);

    const buttonDisabled = error !== '' || isProcessingClick || text === '';
    const buttonDisabledInfoIconVisible = text === '';
    const summaryCounterInfoIconVisible = summaryCounterTooltip !== '';

    return <Accordion
        disabled={disabled}
        expanded={expanded}
        onChange={onExpandedChange}
        style={{ margin: 10 }}
    >
        <AccordionSummary
            expandIcon={<ExpandMore />}
        >
            <Grid container alignItems='center' justifyContent='flex-end' width='100%'>
                <Grid size='grow'>
                    <Typography style={{ fontWeight: 'bold' }}>{summaryTextLeft}</Typography>
                </Grid>
                <Grid size='auto'>
                    <Typography style={{
                        color: summaryCounterColor,
                        fontWeight: 'bold'
                    }}>
                        {summaryCounter}
                    </Typography>
                </Grid>
                {summaryCounterInfoIconVisible && <Grid size='auto'>
                    <InfoIcon infoLabel={summaryCounterTooltip} />
                </Grid>}
            </Grid>
        </AccordionSummary>
        <AccordionDetails>
            <Grid container alignItems='center' spacing={2} direction="column">
                {children}
                <Grid style={{ width: '100%' }}>
                    <TextField
                        inputRef={inputRef}
                        label={textfieldEmptyText}
                        variant="outlined"
                        value={text}
                        error={error !== ''}
                        helperText={error}
                        onKeyDown={handleKeyDown}
                        onChange={(e) => setText(e.target.value)}
                        fullWidth
                    />
                </Grid>
                <Grid style={{ width: '100%' }}>
                    <Grid container
                        alignItems="center"
                    >
                        <Grid size='grow' >
                            <Button variant="contained"
                                disabled={buttonDisabled}
                                color="primary"
                                onClick={handleClick}
                                fullWidth
                            >
                                {isProcessingClick ? <CircularProgress /> : buttonText}
                            </Button>
                        </Grid>
                        {buttonDisabledInfoIconVisible && <Grid size='auto' style={{ textAlign: 'center' }}>
                            <InfoIcon infoLabel={buttonTooltip} />
                        </Grid>}
                    </Grid>
                </Grid>
                {childrenBelowButton}
                <Grid style={{ width: '100%' }}>
                    <Typography variant='caption'>
                        <Link href={clickableLink} target="_blank" rel="noopener noreferrer" >
                            {clickableLinkText}
                        </Link>
                    </Typography>
                </Grid>
            </Grid>
        </AccordionDetails>
    </Accordion >
}
